import { useState, useRef, useCallback, useEffect } from 'react';
import { eventService } from '../services';

const DEBOUNCE_MS = 1500;
const STATUS_RESET_MS = 3000;

/**
 * useAutoSave - Auto-saves event form data on blur/debounce
 *
 * Create mode: auto-creates a draft when title is typed, returns new eventId
 * Edit mode: calls updateEvent with only changed fields
 */
export default function useAutoSave({ eventId, formData, houseRules, partySupplies, volunteerTasks, isPriceLocked, maxAttendees }) {
  const [saveStatus, setSaveStatus] = useState('idle');
  const [autoSaveEventId, setAutoSaveEventId] = useState(eventId || null);

  const lastSavedRef = useRef(null);
  const debounceRef = useRef(null);
  const statusTimeoutRef = useRef(null);
  const isSavingRef = useRef(false);
  const isCreatingRef = useRef(false);

  // Keep autoSaveEventId in sync if eventId prop changes (e.g. navigated to edit mode)
  useEffect(() => {
    if (eventId) setAutoSaveEventId(eventId);
  }, [eventId]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
    };
  }, []);

  const dollarsToCents = (dollars) => {
    const val = parseFloat(dollars);
    return isNaN(val) ? 0 : Math.round(val * 100);
  };

  const buildEventData = useCallback(() => {
    const data = {};

    // Only include fields that have actual content (backend skips null/missing fields)
    if (formData.title.trim()) data.title = formData.title.trim();
    if (formData.description.trim()) data.description = formData.description.trim();
    if (formData.location.trim()) data.locationAddress = formData.location.trim();
    if (formData.locationLat != null) data.locationLatitude = formData.locationLat;
    if (formData.locationLng != null) data.locationLongitude = formData.locationLng;
    if (formData.locationCity) data.locationCity = formData.locationCity;
    // Venue is optional — send it (incl. empty string to clear) when the field is defined.
    if (formData.venueName !== undefined) data.venueName = formData.venueName || '';
    if (formData.venueAddress !== undefined) data.venueAddress = formData.venueAddress || '';
    if (formData.mode) data.mode = formData.mode;
    if (formData.fundingType) data.fundingType = formData.fundingType;
    if (typeof formData.isPrivate === 'boolean') data.isPrivate = formData.isPrivate;
    if (formData.fundingGoal) data.fundingGoalCents = dollarsToCents(formData.fundingGoal);
    if (formData.ticketPrice && !isPriceLocked) data.ticketPriceCents = dollarsToCents(formData.ticketPrice);
    if (typeof formData.isExternalTicketing === 'boolean') data.isExternalTicketing = formData.isExternalTicketing;
    if (formData.externalTicketUrl !== undefined) data.externalTicketUrl = formData.externalTicketUrl || '';
    if (formData.externalTicketDisplayPrice !== undefined) data.externalTicketDisplayPrice = formData.externalTicketDisplayPrice || '';
    if (formData.dateTime) data.dateTime = new Date(formData.dateTime).toISOString();
    if (formData.endDateTime) data.endDateTime = new Date(formData.endDateTime).toISOString();
    if (formData.fundingDeadline) data.fundingDeadline = new Date(formData.fundingDeadline).toISOString();

    const filteredRules = houseRules.filter((r) => r.trim());
    if (filteredRules.length > 0) data.houseRules = filteredRules;

    const filteredSupplies = partySupplies.filter((s) => s.text.trim());
    if (filteredSupplies.length > 0) data.partySupplies = filteredSupplies.map((s) => s.text.trim());

    const filteredVolunteers = volunteerTasks.filter((v) => v.text.trim());
    if (filteredVolunteers.length > 0) data.volunteerTasks = filteredVolunteers.map((v) => v.text.trim());

    const parsedMax = parseInt(maxAttendees, 10);
    data.maxAttendees = !isNaN(parsedMax) && parsedMax > 0 ? parsedMax : 0;

    return data;
  }, [formData, houseRules, partySupplies, volunteerTasks, isPriceLocked, maxAttendees]);

  const getCurrentSnapshot = useCallback(() => {
    return JSON.stringify({
      formData,
      houseRules,
      partySupplies: partySupplies.map((s) => s.text),
      volunteerTasks: volunteerTasks.map((v) => v.text),
      maxAttendees,
    });
  }, [formData, houseRules, partySupplies, volunteerTasks, maxAttendees]);

  const hasChanges = useCallback(() => {
    if (!lastSavedRef.current) return true;
    return getCurrentSnapshot() !== lastSavedRef.current;
  }, [getCurrentSnapshot]);

  const setStatusWithReset = useCallback((status) => {
    setSaveStatus(status);
    if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
    if (status === 'saved' || status === 'error') {
      statusTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), STATUS_RESET_MS);
    }
  }, []);

  const performSave = useCallback(async () => {
    if (isSavingRef.current) return;
    if (!hasChanges()) return;

    const currentId = autoSaveEventId;

    // Create mode: need title to create a draft
    if (!currentId) {
      if (!formData.title.trim() || isCreatingRef.current) return;

      isCreatingRef.current = true;
      isSavingRef.current = true;
      setStatusWithReset('saving');

      try {
        // Only send required fields + whatever the user has actually filled in
        const eventData = {
          title: formData.title.trim(),
          dateTime: formData.dateTime
            ? new Date(formData.dateTime).toISOString()
            : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          fundingDeadline: formData.fundingDeadline
            ? new Date(formData.fundingDeadline).toISOString()
            : new Date(Date.now() + 6 * 24 * 60 * 60 * 1000).toISOString(),
          mode: formData.mode,
          fundingType: formData.fundingType || 'party_starter',
          isPrivate: !!formData.isPrivate,
          fundingGoalCents: dollarsToCents(formData.fundingGoal) || 0,
          ticketPriceCents: dollarsToCents(formData.ticketPrice) || 0,
        };
        // Only include optional fields if user has typed something
        if (formData.description.trim()) eventData.description = formData.description.trim();
        if (formData.location.trim()) eventData.locationAddress = formData.location.trim();
        if (formData.locationLat != null) eventData.locationLatitude = formData.locationLat;
        if (formData.locationLng != null) eventData.locationLongitude = formData.locationLng;
        if (formData.locationCity) eventData.locationCity = formData.locationCity;
        if (formData.venueName) eventData.venueName = formData.venueName;
        if (formData.venueAddress) eventData.venueAddress = formData.venueAddress;
        const filteredRules = houseRules.filter((r) => r.trim());
        if (filteredRules.length > 0) eventData.houseRules = filteredRules;
        const filteredSupplies = partySupplies.filter((s) => s.text.trim());
        if (filteredSupplies.length > 0) eventData.partySupplies = filteredSupplies.map((s) => s.text.trim());
        const filteredVolunteers = volunteerTasks.filter((v) => v.text.trim());
        if (filteredVolunteers.length > 0) eventData.volunteerTasks = filteredVolunteers.map((v) => v.text.trim());
        const parsedMaxCreate = parseInt(maxAttendees, 10);
        if (!isNaN(parsedMaxCreate) && parsedMaxCreate > 0) eventData.maxAttendees = parsedMaxCreate;

        const result = await eventService.createEvent(eventData);
        if (result.success) {
          const newId = result.data.id;
          setAutoSaveEventId(newId);
          lastSavedRef.current = getCurrentSnapshot();
          setStatusWithReset('saved');
          return newId;
        } else {
          setStatusWithReset('error');
        }
      } catch {
        setStatusWithReset('error');
      } finally {
        isSavingRef.current = false;
        isCreatingRef.current = false;
      }
      return null;
    }

    // Edit mode: update existing event
    isSavingRef.current = true;
    setStatusWithReset('saving');

    try {
      const eventData = buildEventData();
      const result = await eventService.updateEvent(currentId, eventData);
      if (result.success) {
        lastSavedRef.current = getCurrentSnapshot();
        setStatusWithReset('saved');
      } else {
        setStatusWithReset('error');
      }
    } catch {
      setStatusWithReset('error');
    } finally {
      isSavingRef.current = false;
    }
  }, [autoSaveEventId, formData, houseRules, partySupplies, volunteerTasks, hasChanges, getCurrentSnapshot, buildEventData, setStatusWithReset]);

  const triggerSave = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    performSave();
  }, [performSave]);

  const debouncedSave = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      performSave();
    }, DEBOUNCE_MS);
  }, [performSave]);

  // Mark current form state as synced with server (prevents spurious saves after fetch)
  const markSynced = useCallback(() => {
    lastSavedRef.current = getCurrentSnapshot();
  }, [getCurrentSnapshot]);

  // Initialize lastSavedRef when first loading in edit mode
  useEffect(() => {
    if (eventId && !lastSavedRef.current) {
      lastSavedRef.current = getCurrentSnapshot();
    }
  }, [eventId, getCurrentSnapshot]);

  return {
    saveStatus,
    triggerSave,
    debouncedSave,
    autoSaveEventId,
    markSynced,
  };
}
